import React, { useState } from 'react';
import { getAllTemplates, getTemplateById, generateContentBrief, formatBriefForDisplay } from '../../services/contentTemplatesService';

const TemplateSelector = ({ onTemplateSelect, selectedTemplateId, brandInfo }) => {
  const [showPreview, setShowPreview] = useState(null);
  const templates = getAllTemplates();

  const handleTemplateSelect = (templateId) => {
    const template = getTemplateById(templateId);
    if (template) {
      const brief = generateContentBrief(template, brandInfo);
      onTemplateSelect(templateId, brief);
    }
  };

  const handlePreview = (templateId) => {
    setShowPreview(templateId === showPreview ? null : templateId);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Choose a Content Template
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Select a ready-to-use template to guide influencers on creating content. You can customize it after selection.
        </p>
      </div>

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((template) => (
          <div key={template.id} className="relative">
            <div
              className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                selectedTemplateId === template.id
                  ? 'border-primary bg-primary-50 shadow-md'
                  : 'border-gray-200 hover:border-primary hover:shadow-sm'
              }`}
              onClick={() => handleTemplateSelect(template.id)}
            >
              {/* Template Icon & Name */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-3xl">{template.icon}</span>
                  <div>
                    <h4 className="font-semibold text-gray-900">{template.name}</h4>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                      {template.category}
                    </span>
                  </div>
                </div>
                {selectedTemplateId === template.id && (
                  <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600 mb-3">
                {template.description}
              </p>

              {/* Preview Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreview(template.id);
                }}
                className="text-sm text-primary hover:text-primary-dark font-medium"
              >
                {showPreview === template.id ? 'Hide Preview' : 'Preview Template'}
              </button>
            </div>

            {/* Preview Panel */}
            {showPreview === template.id && (
              <div className="mt-2 border border-gray-200 rounded-lg p-4 bg-white shadow-lg">
                <div className="space-y-3">
                  <div>
                    <h5 className="text-sm font-semibold text-gray-900 mb-1">Content Structure:</h5>
                    <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 p-2 rounded">
                      {template.contentStructure}
                    </pre>
                  </div>

                  <div>
                    <h5 className="text-sm font-semibold text-gray-900 mb-1">Caption Template:</h5>
                    <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 p-2 rounded">
                      {template.captionTemplate}
                    </pre>
                  </div>

                  <div>
                    <h5 className="text-sm font-semibold text-gray-900 mb-1">Requirements ({template.requirements.length}):</h5>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {template.requirements.slice(0, 5).map((req, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-primary mr-1">•</span>
                          {req}
                        </li>
                      ))}
                      {template.requirements.length > 5 && (
                        <li className="text-gray-500 italic">
                          +{template.requirements.length - 5} more requirements
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Custom Brief Option */}
      <div className="border-t border-gray-200 pt-6">
        <div
          className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
            selectedTemplateId === 'custom'
              ? 'border-primary bg-primary-50 shadow-md'
              : 'border-gray-200 hover:border-primary hover:shadow-sm'
          }`}
          onClick={() => onTemplateSelect('custom', null)}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-3xl">✏️</span>
              <div>
                <h4 className="font-semibold text-gray-900">Create Custom Brief</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Write your own content guidelines from scratch
                </p>
              </div>
            </div>
            {selectedTemplateId === 'custom' && (
              <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </div>
      </div>

      {/* Optional Examples Section */}
      {selectedTemplateId && selectedTemplateId !== 'custom' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h5 className="text-sm font-medium text-blue-900 mb-1">
                Template Selected!
              </h5>
              <p className="text-sm text-blue-700">
                Your content brief has been auto-filled with this template. You can customize it in the next section or add your own example posts (optional).
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateSelector;
